import { Box, Button, Tooltip, Typography } from '@mui/material'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ColumnOrderList from '../../../Common/ColumnOrderList'
import { CustomTab, CustomTabs } from '../../../Common/MuiTabs'
import ExportColumnsPopup from '../../../Entity/ExportColumnsPopup'
import { UploadManagerHeaderProps } from '../interface'
import UploadManagerSearch from './UploadManagerSearch'

export const UploadManagerHeader = ({
    searchBy,
    setSearchBy,
    filteredRowsCount,
    totalRecords,
    onUploadClick,
    columnVisibilityList,
    onUpdateColumns,
    onSaveColumns,
    onResetColumns,
    handleExportEntities,
    enableBulkUpload,
    onSearchAction,
}: UploadManagerHeaderProps): React.ReactElement => {
    const { t } = useTranslation()
    const [openExportPopup, setOpenExportPopup] = useState(false)
    const [searchText, setSearchText] = useState('')

    const handleExport = useCallback(
        (selectedColumns: string[]) => {
            handleExportEntities(selectedColumns)
            setOpenExportPopup(false)
        },
        [handleExportEntities],
    )

    const searchColumns = useMemo(
        () => [
            {
                attributeName: 'FILENAME',
                columnName: t('uploadManager.searchBy.filename'),
            },
            {
                attributeName: 'USER',
                columnName: t('uploadManager.searchBy.user'),
            },
            {
                attributeName: 'TEMPLATE',
                columnName: t('uploadManager.searchBy.type'),
            },
            {
                attributeName: 'STATUS',
                columnName: t('uploadManager.searchBy.status'),
            },
        ],
        [t],
    )

    const handleColumnSelect = useCallback(
        (col: { attributeName: string; columnName: string } | null) =>
            setSearchBy(col?.attributeName ?? ''),
        [setSearchBy],
    )

    const handleSearchChange = useCallback((value: string) => {
        setSearchText(value)
    }, [])

    const handleTriggerSearch = useCallback(
        (text?: string) => {
            onSearchAction(text ?? searchText)
        },
        [onSearchAction, searchText],
    )

    return (
        <>
            <Box
                sx={{
                    px: '10px',
                    pb: '0px',
                    borderBottom: '1px solid rgb(229, 240, 242)',
                    marginBottom: '5px',
                }}
            >
                <CustomTabs
                    value={0}
                    TabIndicatorProps={{ children: <span /> }}
                >
                    <CustomTab
                        sx={{ marginLeft: '5px' }}
                        label={t('uploadManager.header.title')}
                        value={0}
                    />
                </CustomTabs>
            </Box>
            <Box
                sx={{
                    mx: '3px',
                    px: 2,
                    py: 1.5,
                    bgcolor: '#4fd0dd',
                    borderTopLeftRadius: 8,
                    borderTopRightRadius: 8,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}
            >
                <UploadManagerSearch
                    searchableColumns={searchColumns}
                    selectedColumn={
                        searchColumns.find(
                            (c) => c.attributeName === searchBy,
                        ) || null
                    }
                    searchValue={searchText}
                    onSearchChange={handleSearchChange}
                    onColumnSelect={handleColumnSelect}
                    triggerSearch={handleTriggerSearch}
                />

                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: '14px',
                            color: '#7a7a7a',
                            fontWeight: 600,
                        }}
                    >
                        {filteredRowsCount} of {totalRecords}{' '}
                        {t('uploadManager.header.records')}
                    </Typography>

                    <ColumnOrderList
                        list={columnVisibilityList}
                        handleUpdateList={onUpdateColumns}
                        handleOnSaveList={onSaveColumns}
                        handleOnResetList={onResetColumns}
                    />
                    <Button
                        id="upload-manager-header-export-button"
                        onClick={() => setOpenExportPopup(true)}
                        sx={{ minWidth: 0, width: 0 }}
                    >
                        <Tooltip
                            title={t('uploadManager.header.export')}
                            placement="top"
                        >
                            <img
                                src="/assets/images/icons/file_save.svg"
                                alt="Save File"
                            />
                        </Tooltip>
                    </Button>
                    {enableBulkUpload && (
                        <Button
                            id="upload-manager-header-upload-button"
                            onClick={onUploadClick}
                            sx={{ minWidth: 0, width: 0 }}
                        >
                            <Tooltip
                                title={t('uploadManager.header.upload')}
                                placement="top"
                            >
                                <img
                                    src="/assets/images/icons/upload-icon.svg"
                                    alt="Upload"
                                />
                            </Tooltip>
                        </Button>
                    )}

                    {openExportPopup && (
                        <ExportColumnsPopup
                            open
                            onClose={() => setOpenExportPopup(false)}
                            columns={columnVisibilityList}
                            onExport={handleExport}
                        />
                    )}
                </Box>
            </Box>
        </>
    )
}
